import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  DatePicker,
  DatePickerToolbar,
  DatePickerToolbarProps,
  StaticDatePicker,
} from "@mui/x-date-pickers";
import { ICalendar } from "../../../interfaces/globalComponents/calendar/ICalendar";
import { Dayjs } from "dayjs";
import { Box } from "@mui/material";
import { Edit } from "@mui/icons-material";
import { IconButton } from "../buttons/material/iconButton/IconButton";
import useUser from "../../../hooks/user/useUser";

function CustomToolbar(props: DatePickerToolbarProps<Dayjs>) {
  const theme = useSelector((state: RootState) => state.theme);
  const [showInput, setShowInput] = useState(false);
  const { user } = useUser();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid",
        borderColor: theme.componentColors.outline.outlineVariant,
      }}
      className="p-4"
    >
      {showInput ? (
        <DatePicker
          {...props}
          sx={{
            ".MuiIconButton-root": {
              display: "none",
            },
          }}
          format={user.dateFormat ?? null}
        />
      ) : (
        <DatePickerToolbar
          sx={{
            span: {
              textTransform: "none",
            },
          }}
          {...props}
        />
      )}

      <IconButton
        onClick={() => {
          setShowInput((prev) => !prev);
        }}
        icon={
          <Edit
            sx={{
              color: theme.componentColors.surface.onSurface,
            }}
          />
        }
      />
    </Box>
  );
}

export const Calendar = ({
  onChange,
  views,
  value,
  minDate,
  maxDate,
}: ICalendar) => {
  const theme = useSelector((state: RootState) => state.theme);

  return (
    <StaticDatePicker
      sx={{
        "&.MuiPickersLayout-root": {
          backgroundColor: "transparent",
          display: "flex",
          flexDirection: "column",
        },
        "&.MuiButtonBase-root-MuiPickersDay-root.Mui-selected": {
          backgroundColor: theme.componentColors.primary.primary,
          color: theme.componentColors.primary.onPrimary,
        },
        ".css-uj7llv-MuiButtonBase-root-MuiPickersDay-root.Mui-selected": {
          backgroundColor: theme.componentColors.primary.primary,
          color: theme.componentColors.primary.onPrimary,
        },
        ".css-1n97jkc-MuiButtonBase-root-MuiPickersDay-root.Mui-selected": {
          backgroundColor: theme.componentColors.primary.primary,
          color: theme.componentColors.primary.onPrimary,
        },
        ".css-uj7llv-MuiButtonBase-root-MuiPickersDay-root:not(.Mui-selected)":
          {
            color: theme.componentColors.primary.primary,
            borderColor: theme.componentColors.primary.primary,
          },

        ".css-1at5je5-MuiPickersYear-yearButton.Mui-selected": {
          backgroundColor: theme.componentColors.primary.primary,
          color: theme.componentColors.primary.onPrimary,
        },
        ".css-1at5je5-MuiPickersYear-yearButton.Mui-selected:hover": {
          backgroundColor: theme.componentColors.primary.primary,
          color: theme.componentColors.primary.onPrimary,
        },
        ".css-pc9az8-MuiPickersLayout-root .css-1at5je5-MuiPickersYear-yearButton.Mui-selected":
          {
            backgroundColor: theme.componentColors.primary.primary,
            color: theme.componentColors.primary.onPrimary,
          },
        ".css-dplwbx-MuiPickersCalendarHeader-label": {
          color: theme.componentColors.surface.onSurface,
        },
        ".css-1tkx1wf-MuiSvgIcon-root-MuiPickersCalendarHeader-switchViewIcon":
          {
            color: theme.componentColors.surface.onSurface,
          },
        ".css-1vooibu-MuiSvgIcon-root": {
          color: theme.componentColors.surface.onSurface,
        },
        ".css-1q4hfe7-MuiButtonBase-root-MuiIconButton-root-MuiPickersArrowSwitcher-button.Mui-disabled":
          {
            opacity: 0.5,
          },
        ".css-1n97jkc-MuiButtonBase-root-MuiPickersDay-root": {
          color: theme.componentColors.surface.onSurface,
        },
        ".css-1n97jkc-MuiButtonBase-root-MuiPickersDay-root:focus.Mui-selected":
          {
            backgroundColor: theme.componentColors.primary.primary,
            color: theme.componentColors.primary.onPrimary,
          },
        ".MuiDialogActions-root": {
          display: "none",
        },
        ".css-nk89i7-MuiPickersCalendarHeader-root": {
          width: "100%",
          paddingLeft: 0,
          paddingRight: 0,
        },
        ".css-16d8vrz-MuiPickersToolbar-root-MuiDatePickerToolbar-root": {
          textAlign: "left",
        },
        ".css-vwef7a-MuiPickersToolbar-content": {
          textAlign: "left",
          width: "auto",
        },
        "&.MuiPickersLayout-root .MuiPickersYear-yearButton.Mui-selected": {
          backgroundColor: theme.componentColors.primary.primary,
          color: theme.componentColors.primary.onPrimary,
        },
        "& .MuiButtonBase-root-MuiPickersDay-root.Mui-selected:hover": {
          backgroundColor: theme.componentColors.primary.primary,
          color: theme.componentColors.primary.onPrimary,
        },
        "&.MuiPickersLayout-root .MuiButtonBase-root-MuiPickersDay-root.Mui-selected":
          {
            backgroundColor: theme.componentColors.primary.primary,
            color: theme.componentColors.primary.onPrimary,
          },
        "@media (min-width: 300px)": {
          ".css-nk89i7-MuiPickersCalendarHeader-root": {
            width: "100%",
          },
        },
        "@media (min-width: 1800px)": {
          ".css-nk89i7-MuiPickersCalendarHeader-root": {
            width: "100%",
          },
        },
      }}
      defaultValue={value}
      minDate={minDate}
      views={views}
      onChange={onChange}
      maxDate={maxDate}
      slots={{ toolbar: CustomToolbar }}
    />
  );
};
